import { useMemo, useEffect, useState } from 'react';
import { Layers, X, ChevronDown, ChevronUp } from 'lucide-react';
import { CalendarEvent } from '../../types/calendar';
import { getSourceInfo, SourceInfo } from '../../utils/sourceInfo';
import { supabase } from '../../lib/supabase';

interface SourceFilterProps {
  events: CalendarEvent[];
  calendarId?: string;
  selectedSources: string[];
  onSelectionChange: (sources: string[]) => void;
}

interface SourceWithCount extends SourceInfo {
  count: number;
}

function SourceFavicon({ src, label }: { src: string; label: string }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span className="w-4 h-4 rounded-sm bg-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-500 leading-none flex-shrink-0">
        {label.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={label}
      className="w-4 h-4 rounded-sm object-contain flex-shrink-0"
      onError={() => setFailed(true)}
    />
  );
}

export default function SourceFilter({ events, calendarId, selectedSources, onSelectionChange }: SourceFilterProps) {
  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>([]);

  useEffect(() => {
    if (events.length > 0 || !calendarId) return;

    supabase
      .from('calendar_events')
      .select('id, title, start_datetime, end_datetime, source')
      .eq('calendar_id', calendarId)
      .in('sync_status', ['synced', 'pending_google_sync'])
      .not('source', 'is', null)
      .then(({ data }) => {
        if (data && data.length > 0) {
          setLocalEvents(data.map(e => ({
            id: e.id,
            title: e.title,
            start: new Date(e.start_datetime),
            end: new Date(e.end_datetime),
            source: e.source || undefined,
          })));
        }
      });
  }, [events.length, calendarId]);

  const allEvents = events.length > 0 ? events : localEvents;

  const sources = useMemo(() => {
    const sourceMap = new Map<string, SourceWithCount>();

    allEvents.forEach(event => {
      if (!event.source) return;
      const normalized = event.source.toLowerCase().trim();
      const existing = sourceMap.get(normalized);
      if (existing) {
        existing.count += 1;
      } else {
        sourceMap.set(normalized, { ...getSourceInfo(event.source), count: 1 });
      }
    });

    return Array.from(sourceMap.values()).sort((a, b) => b.count - a.count);
  }, [allEvents]);

  const [expanded, setExpanded] = useState(false);
  const VISIBLE_LIMIT = 5;

  if (sources.length < 2) return null;

  const hasMore = sources.length > VISIBLE_LIMIT;
  const visibleSources = expanded ? sources : sources.slice(0, VISIBLE_LIMIT);
  const hiddenCount = sources.length - VISIBLE_LIMIT;

  const toggleSource = (id: string) => {
    if (selectedSources.includes(id)) {
      onSelectionChange(selectedSources.filter(s => s !== id));
    } else {
      onSelectionChange([...selectedSources, id]);
    }
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">Sources</h4>
        </div>
        {selectedSources.length > 0 && (
          <button
            onClick={clearAll}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>
      <div className="space-y-1">
        {visibleSources.map(source => {
          const isSelected = selectedSources.includes(source.id);
          return (
            <button
              key={source.id}
              onClick={() => toggleSource(source.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                isSelected
                  ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <SourceFavicon src={source.faviconUrl} label={source.label} />
                <span className="truncate font-medium">{source.label}</span>
              </div>
              <span className={`text-xs flex-shrink-0 ml-2 px-1.5 py-0.5 rounded-full ${
                isSelected
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {source.count}
              </span>
            </button>
          );
        })}
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Show all ({hiddenCount} more)
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
